import { Box } from '@mui/material';

// Engine ids in the run-path convention (NOT the registry's `chatgpt`).
export type EngineKey =
  | 'perplexity'
  | 'openai'
  | 'gemini'
  | 'claude'
  | 'mistral'
  | 'ai_overview'
  // Conversations only (D61bis) — Gemini grounded in live Google Search via the
  // Interactions API. Deliberately NOT in `ALL_ENGINES`: that array drives the
  // Brand Perception pickers and there is no `google` BP engine to select.
  | 'google'
  | 'unknown';

// `secondary: true` = a simulated engine (a model + a web-search harness we own).
// Selectable by agency + enterprise, explicit opt-in.
export const ENGINE_META: Record<EngineKey, { label: string; color: string; bg: string; secondary?: boolean }> = {
  perplexity: { label: 'Perplexity', color: '#1C1C1C', bg: '#1C1C1C18' },
  openai: { label: 'ChatGPT', color: '#10A37F', bg: '#10A37F22' },
  gemini: { label: 'Gemini', color: '#4285F4', bg: '#4285F422' },
  claude: { label: 'Claude', color: '#D97757', bg: '#D9775722' },
  mistral: { label: 'Mistral', color: '#FF7000', bg: '#FF700022', secondary: true },
  // Google red, not Google blue — Gemini already owns #4285F4 and the two would
  // be indistinguishable as chart series.
  ai_overview: { label: 'AI Overview', color: '#EA4335', bg: '#EA433522', secondary: true },
  // The conversations Google cell. The label deliberately says ONLY "Google AI
  // Overview" and the UI never names the transport (product decision 2026-09-28):
  // to the user this is the Google interface, simulated and indicative — same
  // framing as BP's `ai_overview`. Google green so it stays readable next to Gemini
  // blue (#4285F4) and AI Overview red (#EA4335); it never shares a chart with
  // `ai_overview` (BP-only) so the near-identical labels cannot collide.
  google: { label: 'Google AI Overview', color: '#34A853', bg: '#34A85322', secondary: true },
  unknown: { label: 'Moteur', color: '#8B9DC3', bg: '#8B9DC322' },
};

// Selectable engines, in DISPLAY order — this array drives left-to-right order
// everywhere engines are listed (onboarding picker, plan cards). AI Overview
// leads; `secondary` marks explicit opt-in and is independent of position.
export const ALL_ENGINES: { id: Exclude<EngineKey, 'unknown'>; label: string; secondary?: boolean }[] = [
  { id: 'ai_overview', label: 'AI Overview', secondary: true },
  { id: 'perplexity', label: 'Perplexity' },
  { id: 'openai', label: 'ChatGPT' },
  { id: 'gemini', label: 'Gemini' },
  { id: 'claude', label: 'Claude' },
  { id: 'mistral', label: 'Mistral', secondary: true },
];

const KNOWN_ENGINE_KEYS = new Set<EngineKey>([
  'perplexity',
  'openai',
  'gemini',
  'claude',
  'mistral',
  'ai_overview',
  'google',
]);

// Maps an engine key to its SVG filename in /public/engines/.
// `openai` → chatgpt.svg (API key name differs from the brand name).
const ENGINE_LOGO_FILE: Partial<Record<EngineKey, string>> = {
  perplexity: 'perplexity',
  openai: 'chatgpt',
  gemini: 'gemini',
  claude: 'claude',
  mistral: 'mistral',
};

export function engineKey(e?: string): EngineKey {
  const k = (e || '').toLowerCase();
  // Normalize variant spellings to the canonical key
  if (k === 'chatgpt') return 'openai';
  return KNOWN_ENGINE_KEYS.has(k as EngineKey) ? (k as EngineKey) : 'unknown';
}

/**
 * Logo for an AI engine. Uses the SVG files in /public/engines/ for all major
 * engines (better fidelity than inline paths). Falls back to AiOverviewLogo or
 * a lettered badge for engines without a file asset.
 */
export function EngineLogo({ engine, size = 20 }: { engine?: string; size?: number }) {
  const k = engineKey(engine);
  const meta = ENGINE_META[k];
  const file = ENGINE_LOGO_FILE[k];

  if (file) {
    return (
      <img
        src={`/engines/${file}.svg`}
        alt={meta.label}
        width={size}
        height={size}
        style={{ display: 'block', objectFit: 'contain' }}
      />
    );
  }
  if (k === 'ai_overview' || k === 'google') {
    return <AiOverviewLogo size={size} />;
  }
  // Unknown engine: lettered badge
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        bgcolor: meta.bg,
        color: meta.color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size * 0.55,
        fontWeight: 700,
        flexShrink: 0,
      }}
    >
      {(meta.label?.[0] ?? '?')}
    </Box>
  );
}

/**
 * Official Mistral mark (10 colored rects, source SVG viewBox 129×91). Rendered
 * in a square box, centered, undistorted. Shared so every EngineLogo copy uses
 * the same asset. Source: assets/genAI engine logo/Mistral.svg.
 */
export function MistralLogo({ size = 20 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 129 91"
      preserveAspectRatio="xMidYMid meet"
      style={{ display: 'block' }}
      aria-label="Mistral"
    >
      <rect x="18.292" y="0" width="18.293" height="18.123" fill="#ffd800" />
      <rect x="91.473" y="0" width="18.293" height="18.123" fill="#ffd800" />
      <rect x="18.292" y="18.121" width="36.586" height="18.123" fill="#ffaf00" />
      <rect x="73.181" y="18.121" width="36.586" height="18.123" fill="#ffaf00" />
      <rect x="18.292" y="36.243" width="91.476" height="18.122" fill="#ff8205" />
      <rect x="18.292" y="54.37" width="18.293" height="18.123" fill="#fa500f" />
      <rect x="54.883" y="54.37" width="18.293" height="18.123" fill="#fa500f" />
      <rect x="91.473" y="54.37" width="18.293" height="18.123" fill="#fa500f" />
      <rect x="0" y="72.504" width="54.89" height="18.123" fill="#e10500" />
      <rect x="73.181" y="72.504" width="54.89" height="18.123" fill="#e10500" />
    </svg>
  );
}

/**
 * Google mark, used for the simulated "AI Overview" engine and for the conversations
 * `google` cell (the AI Overview block lives on Google Search, so the Google G is the
 * honest identifier — there is no standalone AI Overview mark). Shared so every
 * EngineLogo copy uses the same asset. Source: assets/genAI engine logo/AI_overview_logo.svg.
 */
export function AiOverviewLogo({ size = 20 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      preserveAspectRatio="xMidYMid meet"
      style={{ display: 'block' }}
      aria-label="AI Overview"
    >
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}
